import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';

const EmailVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setError('No verification token provided');
        setLoading(false);
        return;
      }

      try {
        // Use API Gateway endpoint for email verification
        const response = await fetch(`https://www.receevit.com/api/users/verify-email?token=${token}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        const data = await response.json();
        console.log('Verification response:', data);
        
        if (data.success) {
          setSuccess(true);
          
          // If user is logged in, update their profile to reflect verification
          if (user) {
            console.log('User is logged in, updating profile...');
            await updateProfile({ emailVerified: true });
          }
          
          // Auto-redirect to dashboard after 3 seconds if user is logged in
          if (user) {
            setTimeout(() => {
              navigate('/portal/dashboard');
            }, 3000);
          }
        } else {
          setError(data.error || 'Email verification failed');
        }
      } catch (error) {
        console.error('Email verification error:', error);
        setError(error.message || 'Email verification failed');
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, [token]);

  const handleGoToLogin = () => {
    navigate('/login');
  };
  
  const handleGoToDashboard = () => {
    navigate('/portal/dashboard');
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Verifying Email... | Receevit®</title>
          <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes" />
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Verifying your email...</h2>
            <p className="text-gray-600">Please wait while we verify your email address.</p>
          </div>
        </div>
      </>
    );
  }

  if (success) {
    return (
      <>
        <Helmet>
          <title>Email Verified! | Receevit®</title>
          <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes" />
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
            <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Email Verified!</h1>
            <p className="text-gray-600 mb-6 text-lg">
              Your email address has been successfully verified. You can now send invoices and access all features!
            </p>
            {user ? (
              <>
                <p className="text-sm text-gray-500 mb-4">Redirecting you to your dashboard...</p>
                <button
                  onClick={handleGoToDashboard}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-6 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  Go to Dashboard Now
                </button>
              </>
            ) : (
              <button
                onClick={handleGoToLogin}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-6 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Go to Login
              </button>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Verification Failed | Receevit®</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <XCircle className="h-20 w-20 text-red-500 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Verification Failed</h1>
          <p className="text-gray-600 mb-6 text-lg">
            {error || 'We were unable to verify your email address. The link may have expired or been used already.'}
          </p>
          <div className="space-y-3">
            {user ? (
              <button
                onClick={handleGoToDashboard}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-6 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-semibold text-lg shadow-lg"
              >
                Go to Dashboard
              </button>
            ) : (
              <button
                onClick={handleGoToLogin}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-6 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-semibold text-lg shadow-lg"
              >
                Go to Login
              </button>
            )}
            <p className="text-sm text-gray-500 mt-4">
              Need help? You can resend the verification email from your dashboard.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmailVerificationPage;
